import React, { useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  GET_COOPERATION_WAREHOUSE_DETAIL,
  FindCooperationWarehouseByIdResponse,
} from '../../cooperation-warehouse/graphql/queries/getCooperationWarehouses';
import { mapCwToForm } from '../utils/cwFormMapper';
import PickupSiteFormDrawer, { SiteAddressForSave } from '../components/PickupSiteFormDrawer';
import SplitStationModal, { SplitSelection } from '../components/SplitStationModal';
import {
  RESOLVE_STATION_SPLIT,
  ResolveStationSplitResponse,
  SplitStationInput,
} from '../graphql/mutations/splitStation';
import {
  CREATE_DRAFT_COOPERATION_WAREHOUSE,
  CreateDraftCooperationWarehouseResponse,
} from '../graphql/mutations/draftCooperationWarehouse';
import { PickupSiteFormValues, PickupSiteFormMode, LinkStatus } from '../types/pickupSite';

interface Options {
  /** edit 模式保存成功后回调（刷新等）。 */
  onSaved?: (cooperationWarehouseId: number) => void;
  /**
   * 是否在编辑态显示「AB地址映射」入口（2.2 需求点2），**默认关**。
   *
   * <p>本 hook 被站点管理列表、司机反馈跟进详情、审核池详情共用，而 PRD 原型只画了
   * **站点管理**那一处 ⇒ 由调用方显式开启。见 PickupSiteFormDrawer.showAbEntry 的说明。
   */
  showAbEntry?: boolean;
}

/**
 * 既有站点查看/编辑器：按 cw id 拉取 → 映射表单值 → Drawer 展示。
 * openViewStation 只读查看；openEditStation 可编辑（更新站点，带 warehouseId 走 saveCooperationWarehouse）。
 * 供审核池详情（历史存量/相似站点 查看）与司机反馈详情（查看站点 / 建联成功后更新站点）共用。
 *
 * 拆分站点（#5）：view/edit 抽屉「拆分站点」→ 选关联地址(type2)+主揽收地址 → 抽屉原地切 create 模式预填
 * （原站点基础信息 + 主地址）→ 保存建新 cw → 自动 resolveStationSplit 迁移选中关联地址 relate + 删主旧 relate。
 */
export function useStationViewer(opts?: Options) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<PickupSiteFormMode>('view');
  const [viewId, setViewId] = useState<number | undefined>(undefined);
  const [hubId, setHubId] = useState<number | undefined>(undefined);
  const [values, setValues] = useState<Partial<PickupSiteFormValues>>({});
  const [address, setAddress] = useState<SiteAddressForSave | undefined>(undefined);
  // 拆分编排状态
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitSourceId, setSplitSourceId] = useState<number | undefined>(undefined);
  /**
   * 站点在库里的建联状态（cw.link_status 原值）。与 values.linkStatus 分开存：
   * 二次建联「更新站点」会把 values.linkStatus 预置成「已建联」作为**显示**，
   * 而抽屉判「该不该走二次建联流转」必须用这个原值，否则流转会被静默跳过。
   */
  const [dbLinkStatus, setDbLinkStatus] = useState<number | undefined>(undefined);
  const [splitCtx, setSplitCtx] = useState<{ movedRelateIds: number[]; primaryOldRelateId: number } | null>(null);
  // 单次保存成功回调：仅本次 open 生效（如二次建联「更新站点」保存后转已建联），触发或关闭后即清空
  const [onSavedOnce, setOnSavedOnce] =
    useState<((cwId: number) => Promise<void> | void) | undefined>(undefined);

  const [loadStation] = useLazyQuery<FindCooperationWarehouseByIdResponse, { id: number }>(
    GET_COOPERATION_WAREHOUSE_DETAIL,
    { fetchPolicy: 'no-cache' },
  );
  const [resolveStationSplit] = useMutation<ResolveStationSplitResponse, { input: SplitStationInput }>(
    RESOLVE_STATION_SPLIT,
  );
  // 管理 tab 新建站点无审核池条目可作来源键，sourceKey 由后端按「登录人 + hub」兜底生成（不传）。
  // 关闭时不删草稿：运营可能已拿该 id 建群，重进需复用同一 id；超 24h 未转正由清理任务回收。
  const [createDraft] = useMutation<CreateDraftCooperationWarehouseResponse, { hubId: number }>(
    CREATE_DRAFT_COOPERATION_WAREHOUSE,
  );

  // 按 id 拉取既有 cw 并映射后打开 drawer；站点不存在(悬挂/已删)→warning，查询异常→error
  const load = async (
    id: number,
    nextMode: 'view' | 'edit',
    warehouseId?: number,
    onceOnSaved?: (cwId: number) => Promise<void> | void,
    presetValues?: Partial<PickupSiteFormValues>,
  ) => {
    try {
      const { data } = await loadStation({ variables: { id } });
      const v = data?.getCooperationWarehouseById;
      if (!v) {
        message.warning(t('pickup.addressReviewPool.siteForm.stationNotFound', { id }));
        return;
      }
      const r = mapCwToForm(v);
      // 先记库里原状态，再让 presetValues 覆盖表单值——顺序反了就取到被覆盖后的值
      setDbLinkStatus(r.values.linkStatus);
      setViewId(id);
      setHubId(warehouseId ?? v.warehouseId ?? undefined);
      // standardized 判据已统一收口到 mapCwToForm（isAddressStandardized：只有 standardize_status===1
      // 才算已标准化），此处不再重复判断。link_status=5 的豁免走下方 skipStandardize，不是改这里。
      setAddress(r.address);
      // presetValues 覆盖映射初值：如二次建联「更新站点」把司机反馈面板已填的群名/群ID 带入弹窗预填
      setValues(presetValues ? { ...r.values, ...presetValues } : r.values);
      setMode(nextMode);
      setSplitCtx(null);
      setOnSavedOnce(() => onceOnSaved);
      setOpen(true);
    } catch {
      message.error(t('pickup.addressReviewPool.siteForm.loadFailed'));
    }
  };

  const openViewStation = (id: number) => load(id, 'view');
  const openEditStation = (
    id: number,
    warehouseId?: number,
    onceOnSaved?: (cwId: number) => Promise<void> | void,
    presetValues?: Partial<PickupSiteFormValues>,
  ) => load(id, 'edit', warehouseId, onceOnSaved, presetValues);

  // 新建站点（管理 tab 灰度入口）：先 createDraft(hub) 预占拿真实 cw.id → 开 create 抽屉；
  // 保存带该 id 转正，onSaved 回调刷新；关闭未保存则删草稿占位。与审核池建站同一套预占语义。
  //
  // 建联状态固定「已建联」且不可改（产品口径）：运营手动建站的前提就是已经联系上客户，
  // 「揽收成功/失败待建联」这类状态要求先有司机上门反馈，手工新建时前提不成立。
  // 已建联会连带要求群ID+群名（PickupSiteFormDrawer 的 requireGroup），符合「已建联=有群」。
  const openCreateStation = async (warehouseId: number) => {
    try {
      const resp = await createDraft({ variables: { hubId: warehouseId } });
      const id = resp.data?.createDraftCooperationWarehouse;
      if (id == null) {
        message.error(t('pickup.addressReviewPool.siteForm.saveFailed'));
        return;
      }
      setViewId(Number(id));
      setHubId(warehouseId);
      setAddress(undefined);
      setDbLinkStatus(undefined);
      setValues({ linkStatus: LinkStatus.Linked });
      setSplitCtx(null);
      setMode('create');
      setOpen(true);
    } catch {
      message.error(t('pickup.addressReviewPool.siteForm.saveFailed'));
    }
  };

  // 关闭抽屉：不删草稿占位。运营可能已拿该 id 建了微信群，删掉会导致重进换 id、群名错位；
  // 保留草稿则重进复用同一 id（后端按 sourceKey + status=DRAFT 复用），超 24h 未转正由清理任务物理删。
  const handleClose = () => {
    setOpen(false);
    // 取消（未保存）→ 清空单次回调，避免二次建联「点开弹窗又取消」后残留、误触发在后续无关保存上
    setOnSavedOnce(undefined);
  };

  // 「拆分站点」按钮 → 打开拆分弹窗（针对当前查看/编辑的站点）
  const handleSplit = () => {
    if (viewId == null) return;
    setSplitSourceId(viewId);
    setSplitOpen(true);
  };

  /**
   * 拆分新站点的建议名：对齐后端命名格式「联系人-街道前两段-邮编-id」，但用**新主地址**与预占 id。
   *
   * 不能沿用原站名——拆出的新站与原站同 hub，而名称查重是同 hub 内同名即冲突，且 Repository 层
   * 还有一道抛 BusinessException 的硬校验，「仍要新建」也绕不过，保存会直接失败。
   * 用预占 id 作后缀天然唯一，运营仍可在表单里改。
   */
  const buildSplitSiteName = (sel: SplitSelection, newId: number): string => {
    const contactName = values.contactPersons?.[0]?.name?.trim();
    const street = (sel.primaryAddress.addressLine1 ?? '').split(' ').filter(Boolean).slice(0, 2).join(' ');
    return [contactName, street, sel.primaryAddress.zipcode, newId]
      .filter((v) => v !== undefined && v !== null && v !== '')
      .join('-');
  };

  // 拆分弹窗确认 → 抽屉原地切 create 模式，预填原站点基础信息 + 新主揽收地址，暂存迁移上下文。
  //
  // 必须先 createDraft 预占真实 cw.id：save 成功分支不回传 originalRequest，
  // PickupSiteFormDrawer 回退取 cooperationWarehouseId prop；此前这里置 undefined，
  // 导致 cwId 为空 → onSaved 不触发 → resolveStationSplit 永不调用，关联地址永远迁不过去。
  const handleSplitConfirm = async (sel: SplitSelection) => {
    if (hubId == null) {
      message.error(t('pickup.addressReviewPool.siteForm.saveFailed'));
      return;
    }
    let newId: number;
    try {
      const resp = await createDraft({ variables: { hubId } });
      const id = resp.data?.createDraftCooperationWarehouse;
      if (id == null) {
        message.error(t('pickup.addressReviewPool.siteForm.saveFailed'));
        return;
      }
      newId = Number(id);
    } catch {
      message.error(t('pickup.addressReviewPool.siteForm.saveFailed'));
      return;
    }
    setValues({
      // 基础信息带入，其他置空（联系人/黑名单/AB/关联/排期/群 由 open-effect 空行 or 留空）
      name: buildSplitSiteName(sel, newId),
      warehouseType: values.warehouseType,
      brand: values.brand,
      subBrand: values.subBrand,
      // 建联状态沿用原站：拆分是把原站的一部分独立出来，建联关系是继承的，不是凭空新建。
      // 且状态字段现已全模式锁定（statusEditable=false），不带值会导致必填校验永远过不去、保存点不动。
      // 例外：原站为「确认无法建联」时不继承，落「已建联」——运营主动把这个地址拆成独立站点，
      // 本身就意味着它要被正常使用；继承 5 会让新站一出生就不可用（不进列表、不排线、不能作合并目标），
      // 而状态字段是锁定的，运营改不回来，等于拆出一个死站点。
      linkStatus: values.linkStatus === LinkStatus.Unreachable ? LinkStatus.Linked : values.linkStatus,
      addressLine1: sel.primaryAddress.addressLine1,
      addressLine2: sel.primaryAddress.addressLine2,
      city: sel.primaryAddress.city,
      state: sel.primaryAddress.state,
      zipcode: sel.primaryAddress.zipcode,
      // 已标准化标记不在此设置：分段地址由 PickupSiteFormBody 的播种 effect 从这四段 + dock 组装成
      // addrFields，standardized 取 addressForSave.standardized（setAddress 传的是原站已标准化地址）
    });
    setAddress(sel.primaryAddress);
    setViewId(newId);
    setSplitCtx({ movedRelateIds: sel.movedRelateIds, primaryOldRelateId: sel.primaryOldRelateId });
    // 清掉源站的库内状态：拆分是建新站（create），没有"库里原状态"这回事，
    // 留着会让抽屉误以为在编辑一个待建联站点
    setDbLinkStatus(undefined);
    setMode('create');
    setSplitOpen(false);
  };

  // 保存成功：若处于拆分流程 → 调 resolveStationSplit 迁移关联地址；否则普通编辑回调
  const handleSaved = async (newCwId: number) => {
    // 草稿已随保存转正（status 3→1），后端按 status=DRAFT 过滤，同 sourceKey 下次点击会正确新建
    setOpen(false);
    if (splitCtx) {
      try {
        const resp = await resolveStationSplit({
          variables: {
            input: {
              newCooperationWarehouseId: newCwId,
              movedRelateIds: splitCtx.movedRelateIds,
              primaryOldRelateId: splitCtx.primaryOldRelateId,
            },
          },
        });
        if (resp.data?.resolveStationSplit) {
          message.success(t('pickup.addressReviewPool.split.done', '拆分完成'));
          // 仅成功才清上下文。失败时保留，运营可重开该站点再保存一次重试迁移——
          // 新站点由 saveCooperationWarehouse 独立提交、不随 resolveStationSplit 回滚，
          // 清掉上下文就只剩一个空壳新站点 + 关联地址还挂在原站，只能手工合并补救。
          setSplitCtx(null);
        } else {
          message.error(t('pickup.addressReviewPool.split.failed', '新站点已创建，但关联地址迁移失败，请检查'));
        }
      } catch {
        message.error(t('pickup.addressReviewPool.split.failed', '新站点已创建，但关联地址迁移失败，请检查'));
      }
    }
    // 单次保存回调（如二次建联转已建联）：先于全局 onSaved 执行；失败则跳过全局刷新，卡片保留供用户重试
    if (onSavedOnce) {
      const once = onSavedOnce;
      setOnSavedOnce(undefined);
      try {
        await once(newCwId);
      } catch {
        return;
      }
    }
    opts?.onSaved?.(newCwId);
  };

  /**
   * 库里就是「确认无法建联」的站点 ⇒ 豁免地址标准化。
   * 必须用 dbLinkStatus（cw 原值）而非 values.linkStatus：后者会被二次建联的 presetValues
   * 预置成「已建联」，读它会让豁免恒不生效。
   */
  // 同时要求 core_hash 齐备：doSave 里「跳过标准化却没有 core_hash」会直接报错返回
  // （后端不兜底、save() 全列覆盖会把命中键写成 NULL）。cw.address_core_hash 可空，
  // 缺失时宁可走正常标准化路径，别把"被拦"退化成"点确定就报错"。
  const isUnreachable =
    dbLinkStatus === LinkStatus.Unreachable && !!values.addressCoreHash;

  const viewerDrawer = (
    <>
      <PickupSiteFormDrawer
        open={open}
        mode={mode}
        // 默认全模式锁定：新建固定「已建联」（见 openCreateStation），编辑/查看沿用站点原状态。
        // 例外（2.1 需求点3）：编辑「确认无法建联(5)」站点时解锁——这是唯一支持人工回改的状态
        // （后端状态机 ALLOWED 里 CONFIRMED_UNABLE→LINKED 早已存在）。解锁后可选项被
        // statusOptions 限制为仅 5/4，改成 4 时群ID+群名称自动变必填（requireGroup），
        // 保存走 submitSecondContact 完成 transition + op6，不裸写 link_status。
        statusEditable={mode === 'edit' && dbLinkStatus === LinkStatus.Unreachable}
        initialValues={values}
        cooperationWarehouseId={viewId}
        warehouseId={mode === 'view' ? undefined : hubId}
        // 灰度判定专用：warehouseId 在 view 模式是刻意的 undefined（不落库开关），
        // 不能拿它判灰度，否则查看态的跟进记录（2.1 需求点7）永远出不来。
        stationHubId={hubId}
        addressForSave={address}
        onClose={handleClose}
        onSaved={handleSaved}
        showAbEntry={opts?.showAbEntry === true}
        // 刷新走**外部回调**、绕开 handleSaved：后者带拆分结算与 onSavedOnce 副作用，
        // 而 AB 映射不是「站点被保存了」。viewId 为空时不通知（没有站点就不会有映射）。
        onAbMapped={() => {
          if (viewId != null) opts?.onSaved?.(viewId);
        }}
        onSplit={mode !== 'create' ? handleSplit : undefined}
        // 拆分建站跳过「命中相同/相似」查重：拆出地址必然命中源站关联地址，是误报
        skipDedupOnSave={splitCtx != null}
        originalLinkStatus={dbLinkStatus}
        /*
         * 「确认无法建联(link_status=5)」豁免地址标准化：这类站点是审核池里地址标准化失败、
         * 两次电联又都联系不到客户，但要保留下来等以后联系上再转已建联的。它的地址本就标准化不了，
         * 若照常拦截，运营连改个名字/联系人都存不进去，整条"保留后续再建联"的路就断了。
         *
         * skipStandardize 会一并隐藏标准化按钮、抑制"请点击标准化后再保存"的提示、放行保存门禁，
         * 并按原样 hash 落库——正是这个场景需要的语义（2.1 需求点1 已建好这条通路，此处复用）。
         *
         * 两个 hash 必须同真同假：后端 buildAnalyzedFromRequest 直接取入参、不兜底计算，
         * 而 save() 是全列覆盖 UPDATE，跳过标准化却没带 hash 会把站点命中键写成 NULL。
         * analysis 缺失时退回 core（与审核池免上门入口同一兜底口径）。
         */
        skipStandardize={isUnreachable}
        presetAddressCoreHash={isUnreachable ? values.addressCoreHash : undefined}
        presetAddressAnalysisHash={
          isUnreachable ? values.addressAnalysisHash || values.addressCoreHash : undefined
        }
      />
      <SplitStationModal
        open={splitOpen}
        cooperationWarehouseId={splitSourceId}
        onClose={() => setSplitOpen(false)}
        onConfirm={handleSplitConfirm}
      />
    </>
  );

  return { openViewStation, openEditStation, openCreateStation, viewerDrawer };
}
